import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

/**
 * CmsPhotoRail — pulls photos of a given category (or featured) from the CMS
 * and renders a horizontal, snap-scrolling rail. Silent no-op when empty.
 *
 * Props:
 *  - category?: string — one of the CMS canonical categories
 *  - tag?: string — filter by free-form tag
 *  - featured?: boolean — only featured photos
 *  - limit?: number — default 12
 *  - title?: string — small kicker above the rail (optional)
 *  - eyebrow?: string — small brand-colored eyebrow
 *  - variant?: "rail" | "grid" — layout
 */
function absUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url}`;
}

export default function CmsPhotoRail({
  category, tag, featured, limit = 12,
  title, eyebrow = "From the community lens",
  variant = "rail",
  className = "",
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (tag) params.set("tag", tag);
    if (featured != null) params.set("featured", String(featured));
    params.set("limit", String(limit));
    setLoading(true);
    http.get(`/cms/photos?${params.toString()}`)
      .then((r) => setPhotos(r.data?.photos || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [category, tag, featured, limit]);

  if (!loading && photos.length === 0) return null;

  return (
    <section
      className={`my-8 ${className}`}
      data-testid={`cms-rail-${category || tag || "featured"}`}
      data-listen-skip
    >
      {(title || eyebrow) && (
        <div className="mb-3 flex items-baseline justify-between">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            / {eyebrow}
          </div>
          {title && <div className="font-serif-display text-lg text-[#151312]">{title}</div>}
        </div>
      )}
      {variant === "grid" ? (
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((p) => (
            <li key={p.id} className="rounded-xl overflow-hidden border border-black/10 bg-black/5">
              <img
                src={absUrl(p.url)} alt={p.caption || p.category}
                loading="lazy"
                className="w-full h-40 object-cover"
                data-testid={`cms-photo-${p.id}`}
              />
              {(p.caption || p.credit) && (
                <div className="px-3 py-2 text-[11px] text-[#3A3830]">
                  {p.caption && <div className="italic">{p.caption}</div>}
                  {p.credit && <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] mt-1">— {p.credit}</div>}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-2 px-2">
          {photos.map((p) => (
            <figure
              key={p.id}
              className="relative snap-start shrink-0 w-[76%] sm:w-[46%] md:w-[32%] rounded-xl overflow-hidden border border-black/10 bg-black/5"
            >
              <img
                src={absUrl(p.url)} alt={p.caption || p.category}
                loading="lazy"
                className="w-full h-56 md:h-64 object-cover"
                data-testid={`cms-photo-${p.id}`}
              />
              <div className="absolute top-2 right-2">
                <ShareButton
                  url={`${window.location.origin}${p.url?.startsWith("http") ? "" : ""}${p.url}`}
                  title={p.caption || `${p.category} · NaturePulse`}
                  text={p.location || p.caption || ""}
                  variant="icon"
                  testid={`cms-photo-share-${p.id}`}
                />
              </div>
              {(p.caption || p.location) && (
                <figcaption className="px-3 py-2 text-[12px] text-[#3A3830]">
                  {p.caption && <div className="italic">{p.caption}</div>}
                  {p.location && (
                    <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] mt-1">
                      {p.location}
                    </div>
                  )}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
